import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { useAuth } from "../contexts/authContext";
import { fetchOrders } from "../api/orders";
import type { OrderRecord } from "../api/orders";

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const OrdersPage = () => {
  const { session } = useAuth();
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!session?.access_token) return;
    fetchOrders(session.access_token)
      .then(setOrders)
      .catch(() => setError("Failed to load orders."))
      .finally(() => setLoading(false));
  }, [session]);

  return (
    <Box sx={{ maxWidth: 640, mx: "auto", px: { xs: 2, md: 4 }, py: 8 }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          mb: 6,
        }}
      >
        Order History
      </Typography>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress sx={{ color: "black" }} />
        </Box>
      )}

      {error && (
        <Typography variant="body2" sx={{ color: "#dc2626" }}>
          {error}
        </Typography>
      )}

      {!loading && !error && orders.length === 0 && (
        <Box sx={{ textAlign: "center", py: 10 }}>
          <Typography variant="body2" sx={{ color: "#737373", mb: 3 }}>
            No purchases yet.
          </Typography>
          <Button
            component={RouterLink}
            to="/"
            sx={{
              color: "black",
              fontSize: "0.75rem",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              textDecoration: "underline",
              "&:hover": { bgcolor: "transparent" },
            }}
          >
            Start Shopping
          </Button>
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {orders.map((order) => (
          <Box key={order.id} sx={{ border: "1px solid #e5e5e5", p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                mb: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: "#737373",
                }}
              >
                {formatDate(order.created_at)}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                ${Number(order.total).toFixed(2)}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {order.items.map((item, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "48px 1fr auto",
                    gap: 2,
                    py: 1.5,
                    borderTop: i === 0 ? "1px solid #f5f5f5" : "none",
                    borderBottom: "1px solid #f5f5f5",
                  }}
                >
                  <Box
                    component="img"
                    src={item.image}
                    alt={item.name}
                    sx={{
                      width: 48,
                      height: 60,
                      objectFit: "cover",
                      bgcolor: "#f5f5f5",
                    }}
                  />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#737373", display: "block" }}
                    >
                      Size: {item.size}
                      {item.quantity > 1 && ` · Qty: ${item.quantity}`}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ${(item.price * item.quantity).toFixed(2)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default OrdersPage;
